import { Form } from "@inertiajs/react"
import { MoreVertical } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useMenuDialogHandoff } from "@/hooks/use-menu-dialog-handoff"

/**
 * DeclineOverflow — UX Spec §1.2 / §2.1.
 *
 * Card overflow menu (`MoreVertical` icon) for Open-type assigned collabs.
 * The menu hosts a single destructive `Decline` item that opens a confirm
 * `Dialog`. Confirming POSTs to `decline_path` via Inertia `<Form>` (Crew
 * rule — never shadcn Form / RHF / zod).
 *
 * Visibility is driven by `decline_path` being non-null on the wire — the
 * backend (`CreatorMyMissionsQuery#decline_path_for`) emits the URL ONLY
 * for Open-type `assigned` rows. When `decline_path === null` the parent
 * card simply doesn't mount this component.
 *
 * A11y:
 *   - Trigger button carries an aria-label scoped to the mission title.
 *   - Confirm dialog focus traps; first focus on Cancel (safer default —
 *     UX Spec §6).
 */
interface DeclineOverflowProps {
  declinePath: string
  missionTitle: string
}

export function DeclineOverflow({
  declinePath,
  missionTitle,
}: DeclineOverflowProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const { finalFocus, yieldFocusToDialog } = useMenuDialogHandoff()

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={t("collabs.decline.cta") + " — " + missionTitle}
              data-testid="collab-card-overflow-trigger"
            >
              <MoreVertical aria-hidden="true" className="size-4" />
            </Button>
          }
        />
        <DropdownMenuContent align="end" finalFocus={finalFocus}>
          <DropdownMenuItem
            variant="destructive"
            onClick={() => {
              yieldFocusToDialog()
              setOpen(true)
            }}
            data-testid="collab-card-decline-item"
          >
            {t("collabs.decline.cta")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="collab-decline-dialog"
          className="sm:max-w-md"
        >
          <DialogHeader>
            <DialogTitle>{t("collabs.decline.title")}</DialogTitle>
            <DialogDescription>{t("collabs.decline.body")}</DialogDescription>
          </DialogHeader>
          <Form
            action={declinePath}
            method="post"
            options={{ preserveScroll: true }}
            onSuccess={() => setOpen(false)}
          >
            {({ processing }) => (
              <>
                <input type="hidden" name="event" value="decline" />
                <DialogFooter>
                  {/* Cancel takes first focus per UX Spec §6 — safer default. */}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setOpen(false)}
                    autoFocus
                    data-testid="collab-decline-cancel"
                  >
                    {t("collabs.decline.cancel")}
                  </Button>
                  <Button
                    type="submit"
                    variant="destructive"
                    disabled={processing}
                    data-testid="collab-decline-confirm"
                  >
                    {t("collabs.decline.confirm")}
                  </Button>
                </DialogFooter>
              </>
            )}
          </Form>
        </DialogContent>
      </Dialog>
    </>
  )
}
